import React from 'react';
import { Link } from 'react-router-dom';
import { showPercent } from '../libs/util';

/**
 * GenericProductCard: Renders a single product card.
 *
 * Supports two card design variants via cardStyle:
 *   - "penguin_secure": Full card with yields, taglines, dual-currency icon stacks
 *   - "btc_swap": Compact card with BTC icon, no yields/tagline
 */
const GenericProductCard = ({ card, productType }) => {
  const cardStyle = card.cardStyle || 'penguin_secure';
  const investUrl = `/portal/invest/trade/${productType}/${card.source}/${card.target}`;

  if (cardStyle === 'btc_swap') {
    return renderBtcSwapCard(card, investUrl);
  }

  return renderPenguinSecureCard(card, investUrl);
};

const getYieldLabel = (card) => {
  if (card.minYield === 0 || !card.minYield) return '0%';
  return `${showPercent(card.minYield, 2)} - ${showPercent(card.maxYield, 2)}`;
};

const renderCurrencyIcon = (card) => {
  const isUsd =
    card.source?.toUpperCase() === 'USD' ||
    card.target?.toUpperCase() === 'USD';

  if (isUsd) {
    return (
      <h1 className="icon-stack pe-card-icon-stack">
        <em
          className={`bg-dark-dim icon ni ni-sign-${card.target}-alt icon-stack-back`}
        ></em>
        <em
          className={`bg-${card.source}-dim icon ni ni-sign-${card.source}-alt icon-stack-front`}
        ></em>
      </h1>
    );
  }

  return (
    <h1 className="icon-stack pe-card-icon-stack">
      <em
        className={`bg-${card.icon || card.source}-dim icon ni ni-sign-${card.icon || card.source}-alt`}
      ></em>
    </h1>
  );
};

// --- Variant A: Penguin Secure style (matches plancc.js) ---
function renderPenguinSecureCard(card, investUrl) {
  return (
    <div className="nk-ivp-card card card-bordered card-full pe-product-card">
      <div className="card-inner-group">
        <div className="card-inner pe-card-inner">
          <div className="pe-card-icon" aria-hidden="true">
            {renderCurrencyIcon(card)}
          </div>

          <div className="nk-ivp-title card-title pe-card-copy">
            <h6 className="data pe-card-title">{card.title}</h6>
            <span className="data pe-card-kicker">Invest</span>
            {card.tagline && (
              <span className="data pe-card-tagline">{card.tagline}</span>
            )}
          </div>

          <div className="nk-ivp-summary card-text pe-card-metric">
            <div>
              <span className="sub-text pe-card-metric-label">
                Annualised Return
              </span>
              <span className="lead-text pe-card-metric-value">
                {getYieldLabel(card)}
              </span>
            </div>
            <div className="nk-ivp-action pe-card-action">
              <Link className="btn btn-primary" to={investUrl}>
                <span>Invest</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Variant B: BTC Swap style (matches btcAccumelator.js) ---
function renderBtcSwapCard(card, investUrl) {
  return (
    <div className="nk-ivp-card card card-bordered card-full pe-product-card">
      <div className="card-inner-group">
        <div className="card-inner pe-card-inner">
          <div className="pe-card-icon" aria-hidden="true">
            {renderCurrencyIcon({ ...card, icon: card.icon || 'btc' })}
          </div>

          <div className="nk-ivp-title card-title pe-card-copy">
            <h6 className="data pe-card-title">{card.title}</h6>
            <span className="data pe-card-kicker">Invest</span>
          </div>

          <div className="nk-ivp-action pe-card-action pe-card-metric">
            <Link className="btn btn-primary" to={investUrl}>
              <span>Invest</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GenericProductCard;
